import { services } from '@config/services';
import type { ExecutionLog } from '../types';
import { integrationApi } from './_base';

/**
 * Execution-logs endpoint. Note: this endpoint lives on the
 * *transformation* microservice (not integration) and is scoped to
 * plugin rows via `hasPluginContext=true`. Merchant-scope is applied
 * server-side from the auth headers; merchantId is passed only for the
 * super-admin "viewing as merchant" case.
 */

export interface ExecutionLogArgs {
  merchantId?: string;
  searchString?: string;
  pluginType?: string;
  status?: 'Success' | 'Failed' | 'Timeout' | '';
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortAsc?: boolean;
}

export type RawExecutionLogsResponse =
  | ExecutionLog[]
  | { executionLogs?: ExecutionLog[]; total?: number };

export interface ExecutionLogsResult {
  logs: ExecutionLog[];
  total: number;
}

export function transformExecutionLogsResponse(
  response: RawExecutionLogsResponse,
): ExecutionLogsResult {
  if (Array.isArray(response)) return { logs: response, total: response.length };
  const logs = response.executionLogs ?? [];
  return { logs, total: response.total ?? logs.length };
}

function buildExecutionLogQuery(args: ExecutionLogArgs): string {
  const p = new URLSearchParams();
  p.set('hasPluginContext', 'true');
  if (args.searchString) p.set('q', args.searchString);
  if (args.pluginType) p.set('pluginType', args.pluginType);
  if (args.status) p.set('status', args.status);
  if (args.page != null) p.set('page', String(args.page));
  if (args.pageSize != null) p.set('pageSize', String(args.pageSize));
  if (args.sortBy) p.set('sortBy', args.sortBy);
  if (args.sortAsc != null) p.set('sortAsc', String(args.sortAsc));
  if (args.merchantId) p.set('merchantId', args.merchantId);
  return p.toString();
}

const executionLogsApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    getExecutionLogs: builder.query<ExecutionLogsResult, ExecutionLogArgs | void>({
      query: (args) => ({
        url: `${services.transformation}/executionLogs?${buildExecutionLogQuery(args ?? {})}`,
        method: 'GET',
      }),
      transformResponse: transformExecutionLogsResponse,
      providesTags: () => [{ type: 'ExecutionLogs', id: 'LIST' }],
    }),
  }),
});

export const { useGetExecutionLogsQuery } = executionLogsApi;
